import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Zap, Crown, Rocket, ArrowRight, Loader2 } from 'lucide-react';

// Feature requirements for each route
const ROUTE_FEATURES = {
  // Core (Starter)
  '/invoices': 'invoicing',
  '/accounts-receivable': 'invoicing',
  '/accounts-payable': 'invoicing',
  '/clients': 'clients',
  '/services': 'services_goods',
  '/analytics': 'basic_reports',
  '/compliance-alerts': 'basic_reports',
  '/tax-documents': 'tax_settings',
  
  // Team (Professional)
  '/employees': 'employees',
  '/portal-settings': 'portal_management',
  
  // Operations (Professional)
  '/equipment': 'equipment',
  '/fleet': 'fleet',
  '/documents': 'documents',
  '/document-templates': 'documents',
  
  // HR (Professional)
  '/training': 'training',
  '/safety-documents': 'safety',
  '/policies': 'policies',
  '/certifications': 'certifications',
  
  // Enterprise
  '/payroll': 'payroll',
  '/union': 'unions',
  '/audit-logs': 'audit_logs',
};

const PLAN_INFO = {
  starter: { label: 'Starter', price: 14, icon: Zap, color: 'bg-blue-500' },
  professional: { label: 'Professional', price: 34, icon: Crown, color: 'bg-violet-500' },
  enterprise: { label: 'Enterprise', price: 69, icon: Rocket, color: 'bg-amber-500' },
};

const FEATURE_TO_PLAN = {
  invoicing: 'starter',
  clients: 'starter',
  services_goods: 'starter',
  basic_reports: 'starter',
  email_invoice: 'starter',
  tax_settings: 'starter',
  employees: 'professional',
  portal_management: 'professional',
  equipment: 'professional',
  fleet: 'professional',
  documents: 'professional',
  training: 'professional',
  safety: 'professional',
  policies: 'professional',
  certifications: 'professional',
  payroll: 'enterprise',
  payroll_automation: 'enterprise',
  unions: 'enterprise',
  advanced_analytics: 'enterprise',
  audit_logs: 'enterprise',
};

/**
 * ProtectedRoute - Wraps routes that require specific features
 */
export const ProtectedRoute = ({ children, feature }) => {
  const { hasFeature, loading, isAdmin } = useSubscription();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth/subscription
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Determine required feature from route if not explicitly provided
  const requiredFeature = feature || ROUTE_FEATURES[location.pathname];

  // If no feature required or user has access, render children
  if (!requiredFeature || isAdmin || hasFeature(requiredFeature)) {
    return children;
  }

  // Show upgrade prompt
  const requiredPlan = FEATURE_TO_PLAN[requiredFeature] || 'professional';
  const planInfo = PLAN_INFO[requiredPlan];
  const PlanIcon = planInfo?.icon || Crown;

  return (
    <div className="flex items-center justify-center min-h-[500px] p-8">
      <Card className="max-w-md text-center">
        <CardHeader>
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center mb-4">
            <Lock className="h-10 w-10 text-amber-600" />
          </div>
          <CardTitle className="text-xl">Upgrade Required</CardTitle>
          <CardDescription className="text-base">
            This feature requires the {planInfo?.label} plan or higher
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Badge className={`${planInfo?.color} text-white px-3 py-1`}>
                <PlanIcon className="h-3.5 w-3.5 mr-1.5" />
                {planInfo?.label} Plan
              </Badge>
            </div>
            <p className="text-2xl font-bold">${planInfo?.price}<span className="text-sm font-normal text-muted-foreground">/month</span></p>
          </div>
          
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Unlock this feature and more by upgrading your subscription.</p>
          </div>

          <div className="flex flex-col gap-2">
            <Button 
              onClick={() => window.location.href = '/pricing'}
              className="w-full"
            >
              View Plans
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * withFeatureGate - HOC to wrap components with feature gating
 */
export const withFeatureGate = (Component, feature) => {
  return function FeatureGatedComponent(props) {
    return (
      <ProtectedRoute feature={feature}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

export default ProtectedRoute;
